import React from 'react';
import {
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  TableProps,
  TableCellProps,
  tableHeadCellStyle,
  TableHeadCellProps,
  Align,
} from './table';
import styled from '../../styles/styled-components';
import { A11yButton } from '../button';
import memoizeOne from 'memoize-one';
import { Glyphicon } from '../icons';
import { theme } from '../../styles/theme';
import { observer } from 'mobx-react';
import { SortOrder, sortBy } from '../../util/sort';

export type DataTableProps<T> = {
  columns: Array<TableColumn<T>>;
  data: T[];
  dataKey: keyof T | ((item: T) => any);
  groupBy?: keyof T;
  sortable?: boolean;
  initialSortField?: ((item: T) => any) | keyof T;
  initialSortOrder?: SortOrder;
  rowSelectedCallback?: (item: T) => any;
  selectedRowItem?: T;
} & TableProps<T>;

export type TableColumn<T> = {
  header: React.ReactChild;
  key?: string;
  sortField?: ((item: T) => any) | keyof T;
  render: (item: T) => React.ReactChild;
  sortOverrideCallback?: (sortOrder: SortOrder) => any;
} & TableCellProps;

type DataTableState<T> = {
  sortField?: ((item: T) => any) | keyof T;
  sortOrder: SortOrder;
  selectedRowItem?: T;
};

type TableHeadCellClickableProps = TableHeadCellProps & {
  sorted: boolean;
  sortOrder: SortOrder;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const HeaderSortButton = styled<{ align?: Align }>(({ align, ...rest }) => (
  <A11yButton {...rest} />
))`
  ${p => tableHeadCellStyle(p.align)};
`;

const SortableHeader = TableHeadCell.extend`
  padding: 0;
`;

const TableHeadCellClickable: React.SFC<TableHeadCellClickableProps> = ({
  children,
  align,
  sorted,
  sortOrder,
  ...rest
}) => (
  <SortableHeader {...rest}>
    <HeaderSortButton align={align}>
      {children}
      {sorted && (
        <Glyphicon
          style={{ verticalAlign: 'middle' }}
          glyph={sortOrder === 'asc' ? 'arrowUp' : 'arrowDown'}
        />
      )}
    </HeaderSortButton>
  </SortableHeader>
);

@observer
export class DataTable<T extends {}> extends React.Component<
  DataTableProps<T>,
  DataTableState<T>
> {
  constructor(props: DataTableProps<T>) {
    super(props);
    this.state = {
      sortField: props.initialSortField,
      sortOrder: props.initialSortOrder || 'asc',
      selectedRowItem: props.selectedRowItem,
    };
  }

  memoizedSort = memoizeOne(sortBy) as <T>(
    data: T[],
    selector: ((item: T) => any) | keyof T,
    order: SortOrder,
    groupBy?: keyof T,
  ) => T[];

  handleHeaderClick = (
    sortField: DataTableState<T>['sortField'] | undefined,
  ) => {
    if (this.compareSortFields(sortField, this.state.sortField)) {
      this.setState(state => ({
        sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      this.setState({ sortField, sortOrder: 'asc' });
    }
  };

  compareSortFields(
    sortField1: DataTableState<T>['sortField'] | undefined,
    sortField2: DataTableState<T>['sortField'] | undefined,
  ) {
    if (!sortField1 || !sortField2) return false;
    return sortField1.toString() === sortField2.toString();
  }
  render() {
    const {
      columns,
      data: unprocessedData,
      dataKey,
      sortable,
      initialSortField,
      initialSortOrder,
      rowSelectable,
      groupBy,
      rowSelectedCallback,
      rowHoverHighlight,
      selectedRowItem,
      ...rest
    } = this.props;

    const {
      sortField: currentSortField,
      sortOrder: currentSortOrder,
    } = this.state;

    const processedData =
      sortable && currentSortField
        ? this.memoizedSort(
            unprocessedData,
            currentSortField,
            currentSortOrder,
            groupBy,
          )
        : unprocessedData;

    var currentSelectedRowItem = this.state.selectedRowItem || selectedRowItem;

    return (
      <Table {...rest}>
        <TableHead>
          <TableRow>
            {columns.map(
              ({
                header,
                key,
                align,
                sortField,
                sortOverrideCallback,
                showOnRowHover,
                ...rest
              }) =>
                sortable && sortField ? (
                  <TableHeadCellClickable
                    key={key || header.toString()}
                    sorted={this.compareSortFields(sortField, currentSortField)}
                    sortOrder={currentSortOrder}
                    onClick={() => {
                      if (sortOverrideCallback) {
                        // Use the future value for the pre-sort callback.
                        if (
                          !this.compareSortFields(sortField, currentSortField)
                        ) {
                          sortOverrideCallback('asc');
                        } else {
                          sortOverrideCallback(
                            this.state.sortOrder == 'asc' ? 'desc' : 'asc',
                          );
                        }
                        return;
                      }
                      this.handleHeaderClick(sortField);
                    }}
                    align={align}
                  >
                    {header}
                  </TableHeadCellClickable>
                ) : (
                  <TableHeadCell key={key || header.toString()} align={align}>
                    {header}
                  </TableHeadCell>
                ),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {processedData.map(item => (
            <TableRow
              key={typeof dataKey == 'function' ? dataKey(item) : item[dataKey]}
              clickable={
                rowSelectedCallback || this.props.rowSelectable ? true : false
              }
              rowHoverHighlight={this.props.rowHoverHighlight}
              style={{
                backgroundColor:
                  item == currentSelectedRowItem
                    ? theme.grayBackground
                    : 'unset',
              }}
              onClick={() => {
                if (this.props.rowSelectable) {
                  this.setState({
                    selectedRowItem: item,
                  });
                }
                if (rowSelectedCallback) {
                  rowSelectedCallback(item);
                }
              }}
            >
              {columns.map(
                ({
                  header,
                  key,
                  render,
                  sortField,
                  title,
                  sortOverrideCallback: sortOverrideCallback,
                  ...cellProps
                }) => (
                  <TableCell
                    key={header.toString() || key}
                    title={this.getTitle(title, item)}
                    {...cellProps}
                  >
                    {render(item)}
                  </TableCell>
                ),
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  private getTitle(title: string | ((item: any) => string), item: any): string {
    switch (typeof title) {
      case 'string':
        return title as string;
      case 'function':
        var titleFunc = title as ((item: any) => string);
        return titleFunc(item);
      default:
        return '';
    }
  }
}
